import { useState, useCallback, useRef, useEffect } from "react";

export type ResizeDirection = 
  | "n" | "s" | "e" | "w" 
  | "ne" | "nw" | "se" | "sw";

interface UseWidgetResizeProps {
  minWidth?: number;
  minHeight?: number;
  onResize?: (width: number, height: number) => void;
  isEnabled: boolean;
}

export function useWidgetResize({
  minWidth = 200,
  minHeight = 150,
  onResize,
  isEnabled,
}: UseWidgetResizeProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [currentSize, setCurrentSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ width: 0, height: 0 });
  const direction = useRef<ResizeDirection | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, dir: ResizeDirection) => {
      if (!isEnabled || !containerRef.current) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const rect = containerRef.current.getBoundingClientRect();
      startPos.current = { x: e.clientX, y: e.clientY };
      startSize.current = { width: rect.width, height: rect.height };
      direction.current = dir;
      setIsResizing(true);
      setCurrentSize({ width: rect.width, height: rect.height });
    },
    [isEnabled]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!direction.current) return;

      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;

      let newWidth = startSize.current.width;
      let newHeight = startSize.current.height;

      // Handle horizontal resizing
      if (direction.current.includes("e")) {
        newWidth = Math.max(minWidth, startSize.current.width + deltaX);
      } else if (direction.current.includes("w")) {
        newWidth = Math.max(minWidth, startSize.current.width - deltaX);
      }

      // Handle vertical resizing
      if (direction.current.includes("s")) {
        newHeight = Math.max(minHeight, startSize.current.height + deltaY);
      } else if (direction.current.includes("n")) {
        newHeight = Math.max(minHeight, startSize.current.height - deltaY);
      }

      setCurrentSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      if (onResize && currentSize.width > 0 && currentSize.height > 0) {
        onResize(currentSize.width, currentSize.height);
      }
      direction.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, minWidth, minHeight, onResize, currentSize]);

  return {
    containerRef,
    isResizing,
    currentSize,
    handleResizeStart,
  };
}
